import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { useCurrentAccount, useDisconnectWallet, useAccounts, useSwitchAccount, useWallets, useConnectWallet, } from '@mysten/dapp-kit';
import { getIcon } from '../utils/getIcon';
import { GlobalWalletModal, GlobalConnectWalletButton, GlobalWalletDropdown, } from 'ui-kit';
const adaptWalletForModal = (wallet) => {
    try {
        const name = wallet.name;
        const accounts = wallet.accounts || [];
        return {
            name,
            accounts,
        };
    }
    catch (error) {
        console.error('Error adapting wallet:', error);
        return {
            name: 'Unknown Wallet',
            accounts: [],
        };
    }
};
const debug = (message) => {
    console.log(message);
};
const WalletContent = (props) => {
    const { currentAccount, onConnectionChange } = props;
    const { mutate: switchAccount } = useSwitchAccount();
    const accounts = useAccounts();
    const { mutate: disconnect } = useDisconnectWallet();
    const wallets = useWallets();
    const { mutate: connectWallet } = useConnectWallet();
    const [showDisconnectPopup, setShowDisconnectPopup] = useState(false);
    const [showWalletModal, setShowWalletModal] = useState(false);
    const [shortAddress, setShortAddress] = useState('connect wallet');
    const [connectedWalletType, setConnectedWalletType] = useState(null);
    const [availableWallets, setAvailableWallets] = useState([]);
    const walletPopUpRef = useRef(null);
    const walletButtonRef = useRef(null);
    useEffect(() => {
        try {
            if (!Array.isArray(wallets) || wallets.length === 0) {
                setAvailableWallets([]);
                return;
            }
            const displayWallets = wallets.map((wallet) => {
                const safeWallet = {
                    id: typeof wallet.name === 'string' ? wallet.name.toLowerCase() : 'unknown',
                    name: wallet.name || 'Unknown Wallet',
                    icon: wallet.icon,
                    available: true,
                    connected: connectedWalletType ===
                        (typeof wallet.name === 'string' ? wallet.name.toLowerCase() : 'unknown'),
                    walletAdapter: adaptWalletForModal(wallet),
                };
                return safeWallet;
            });
            setAvailableWallets(displayWallets);
        }
        catch (error) {
            console.error('Error mapping wallets:', error);
            setAvailableWallets([]);
        }
    }, [wallets, connectedWalletType]);
    useEffect(() => {
        try {
            if (currentAccount) {
                const miniAddress = getShortAddress(currentAccount);
                setShortAddress(miniAddress);
                onConnectionChange === null || onConnectionChange === void 0 ? void 0 : onConnectionChange(true);
                if (Array.isArray(wallets) && wallets.length > 0) {
                    const connectedAdapter = wallets.find((w) => {
                        try {
                            if (!w.accounts || !Array.isArray(w.accounts)) {
                                return false;
                            }
                            return w.accounts.some((acc) => acc && acc.address === currentAccount);
                        }
                        catch (_err) {
                            return false;
                        }
                    });
                    if (connectedAdapter) {
                        const walletName = typeof connectedAdapter.name === 'string'
                            ? connectedAdapter.name.toLowerCase()
                            : null;
                        setConnectedWalletType(walletName);
                    }
                    else {
                        setConnectedWalletType(null);
                    }
                }
                else {
                    setConnectedWalletType(null);
                }
            }
            else {
                setShortAddress('connect wallet');
                setConnectedWalletType(null);
                onConnectionChange === null || onConnectionChange === void 0 ? void 0 : onConnectionChange(false);
            }
        }
        catch (error) {
            console.error('Error processing wallet connection:', error);
            setShortAddress('connect wallet');
            setConnectedWalletType(null);
        }
    }, [currentAccount, wallets, onConnectionChange]);
    useEffect(() => {
        const handleOutsideClick = (event) => {
            const clickedInsidePopup = walletPopUpRef.current && walletPopUpRef.current.contains(event.target);
            const clickedInsideButton = walletButtonRef.current && walletButtonRef.current.contains(event.target);
            if (!clickedInsidePopup && !clickedInsideButton) {
                setShowDisconnectPopup(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, []);
    const getShortAddress = (address) => {
        if (!address || typeof address !== 'string' || address.length < 8) {
            return address || 'Invalid Address';
        }
        const add1 = address.substring(0, 4);
        const add2 = address.substring(address.length - 4);
        return `${add1}....${add2}`;
    };
    const callDisconnect = () => {
        disconnect();
        setShowDisconnectPopup(false);
    };
    const handleConnectClick = () => {
        setShowWalletModal(true);
    };
    const handleConnectWallet = (walletAdapter, walletId) => {
        try {
            if (!walletAdapter || !walletAdapter.name) {
                console.error('Invalid wallet adapter');
                return;
            }
            if (!Array.isArray(wallets) || wallets.length === 0) {
                console.error('No wallets available');
                return;
            }
            const originalWallet = wallets.find((w) => {
                try {
                    return w.name === walletAdapter.name;
                }
                catch (_err) {
                    return false;
                }
            });
            if (!originalWallet) {
                console.error('Original wallet not found');
                return;
            }
            connectWallet({ wallet: originalWallet }, {
                onSuccess: () => {
                    setShowWalletModal(false);
                    setConnectedWalletType(walletId);
                },
                onError: (error) => {
                    console.error('Error connecting to wallet:', error);
                },
            });
        }
        catch (error) {
            console.error('Error in handleConnectWallet:', error);
        }
    };
    return (_jsx(_Fragment, { children: _jsxs("div", { children: [_jsx("div", { ref: walletButtonRef, children: _jsx(GlobalConnectWalletButton, { currentAccount: currentAccount, shortAddress: shortAddress, onConnectClick: handleConnectClick, onDisconnectClick: callDisconnect, showDisconnectPopup: showDisconnectPopup, setShowDisconnectPopup: setShowDisconnectPopup, buttonText: 'Connect Wallet', buttonStyle: props.buttonStyle, walletUpArrow: getIcon('walletUpArrow'), walletDownArrow: getIcon('walletDownArrow') }) }), _jsx(GlobalWalletModal, { isOpen: showWalletModal, onClose: () => setShowWalletModal(false), connectedWalletType: connectedWalletType, displayWallets: availableWallets, onWalletConnect: handleConnectWallet, getShortAddress: getShortAddress, closeIcon: props.theme === 'dark' ? getIcon('darkCrossIcon') : getIcon('close') }), showDisconnectPopup && (_jsx(GlobalWalletDropdown, { accounts: accounts, currentAccount: currentAccount, walletPopUpRef: walletPopUpRef, onAccountSwitch: (accountBasic) => {
                        try {
                            if (!accountBasic || !accountBasic.address) {
                                console.error('Invalid account provided');
                                return;
                            }
                            const fullAccount = accounts.find((a) => {
                                try {
                                    return a && a.address === accountBasic.address;
                                }
                                catch (_err) {
                                    return false;
                                }
                            });
                            if (fullAccount) {
                                switchAccount({ account: fullAccount }, {
                                    onSuccess: () => {
                                        try {
                                            debug(`Switched to ${fullAccount.address}`);
                                            setShowDisconnectPopup(false);
                                        }
                                        catch (err) {
                                            console.error('Error in switch success callback:', err);
                                            setShowDisconnectPopup(false);
                                        }
                                    },
                                    onError: (error) => {
                                        console.error('Error switching account:', error);
                                    },
                                });
                            }
                        }
                        catch (error) {
                            console.error('Error in onAccountSwitch:', error);
                        }
                    }, onDisconnect: callDisconnect, onCopyAddress: (address) => {
                        try {
                            if (address && typeof address === 'string') {
                                navigator.clipboard.writeText(address);
                            }
                        }
                        catch (error) {
                            console.error('Error copying address:', error);
                        }
                    }, onViewExplorer: (address) => {
                        try {
                            if (address && typeof address === 'string') {
                                window.open(`https://suivision.xyz/account/${address}`, '_blank');
                            }
                        }
                        catch (error) {
                            console.error('Error opening explorer:', error);
                        }
                    }, getShortAddress: getShortAddress, disconnectIcon: getIcon('disconnectWalletIcon'), copyIcon: getIcon('whiteCopyIcon'), explorerIcon: getIcon('externalIconWhite') }))] }) }));
};
const AlphalendConnectWallet = (props) => {
    const currentAccount = useCurrentAccount();
    return (_jsx(WalletContent, Object.assign({}, props, { currentAccount: currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address, onConnectionChange: props.onConnectionChange || (() => { }) })));
};
export default AlphalendConnectWallet;
